<template>
  <v-navigation-drawer
    v-model="show"
    right
    temporary
    fixed
    :width="$vuetify.breakpoint.smAndDown ? $vuetify.breakpoint.width : 760"
    class="cdrawer"
  >
    <div v-if="contact" class="cdrawer__wrap">
      <!-- appbar -->
      <div class="cdrawer__bar">
        <button type="button" class="cdrawer__close" title="Cerrar" @click="show = false">
          <hero-icon :icon="XMarkIcon" :size="18" />
        </button>
        <div class="cdrawer__barname">{{ contact.profile.name }}</div>
        <button type="button" class="cdrawer__editbtn" @click="openEdit">
          <hero-icon :icon="PencilIcon" :size="14" /><span>Editar</span>
        </button>
      </div>

      <div class="cdrawer__scroll">
        <!-- profile -->
        <div class="cdrawer__head">
          <div v-if="roleLabels.length" class="cdrawer__roles">
            <span v-for="r in roleLabels" :key="r" class="cdrawer__role">{{ r }}</span>
          </div>
          <div class="cdrawer__rows">
            <a
              v-if="contact.profile.email"
              class="cdrawer__row cdrawer__row--link"
              :href="'mailto:' + contact.profile.email"
              ><hero-icon :icon="EnvelopeIcon" :size="15" /><span>{{ contact.profile.email }}</span></a
            >
            <a
              v-if="contact.profile.phone"
              class="cdrawer__row cdrawer__row--link"
              :href="'tel:' + contact.profile.phone"
              ><hero-icon :icon="PhoneIcon" :size="15" /><span>{{ contact.profile.phone }}</span></a
            >
            <div v-if="contact.profile.address" class="cdrawer__row">
              <hero-icon :icon="MapPinIcon" :size="15" /><span>{{ contact.profile.address }}</span>
            </div>
            <div v-if="contact.profile.dni" class="cdrawer__row">
              <hero-icon :icon="IdIcon" :size="15" /><span>DNI {{ contact.profile.dni }}</span>
            </div>
          </div>
        </div>

        <!-- advisor -->
        <div class="cdrawer__section">
          <div class="cdrawer__label">Asesor asignado</div>
          <v-select
            v-model="contact.advisorId"
            :items="staff"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            placeholder="Seleccionar asesor…"
            class="cdrawer__advisor"
            @change="updateStaff"
          ></v-select>
          <div v-if="contact.advisor" class="cdrawer__advisor-info">
            <hero-icon :icon="PhoneIcon" :size="13" />{{ contact.advisor.number }}
            <span class="cdrawer__dot">·</span>
            <hero-icon :icon="MapPinIcon" :size="13" />{{ contact.advisor.city }}
          </div>
        </div>

        <!-- tabs -->
        <v-tabs
          v-model="tab"
          color="primary"
          slider-color="accent"
          background-color="transparent"
          class="cdrawer__tabs"
        >
          <v-tab>Gestiones</v-tab>
          <v-tab>Búsquedas</v-tab>
          <v-tab>Propiedades</v-tab>
          <v-tab>Impuestos</v-tab>
        </v-tabs>

        <div class="cdrawer__body">
          <!-- GESTIONES -->
          <div v-if="tab === 0" class="cdrawer__reuse">
            <inquiries :owner="contact"></inquiries>
          </div>

          <!-- BÚSQUEDAS -->
          <div v-if="tab === 1">
            <v-textarea
              outlined
              rows="3"
              hide-details
              label="Observaciones para búsquedas del cliente"
              v-model="contact.searchObservations"
              class="cdrawer__obs"
            ></v-textarea>
            <div class="cdrawer__obs-actions">
              <v-btn text color="primary" class="rounded-lg" @click="editUser">
                {{ success ? 'Observaciones guardadas' : 'Guardar observaciones' }}
              </v-btn>
            </div>

            <div class="cdrawer__searchhead">
              <span>{{ searches.length }} búsqueda(s) asignada(s)</span>
              <create :userId="contact.id" @reload="fetchSearches"></create>
            </div>
            <div class="cdrawer__searchlist">
              <search-card
                v-for="(search, idx) in searches"
                :key="search.id"
                :search="search"
                :colored="!(idx % 2)"
                @reload="fetchSearches"
              ></search-card>
            </div>
          </div>

          <!-- PROPIEDADES -->
          <div v-if="tab === 2" class="cdrawer__reuse">
            <properties :ownerId="contact.id"></properties>
          </div>

          <!-- IMPUESTOS -->
          <div v-if="tab === 3" class="cdrawer__reuse">
            <taxes :userId="contact.id"></taxes>
          </div>
        </div>
      </div>
    </div>

    <edit
      v-if="contactId"
      :key="contactId"
      ref="edit"
      :contact_id="contactId"
      @reload="refetch"
    ></edit>
  </v-navigation-drawer>
</template>

<script>
import { Contact } from "@/graphql/queries/directory";
import { Staff } from "@/graphql/queries/staff";
import { Searches } from "@/graphql/queries/searches";
import { AttachStaff } from "@/graphql/mutations/staff";
import { EditUser } from "@/graphql/mutations/user";

import HeroIcon from "@/components/shared/HeroIcon";
import Inquiries from "@/components/directories/Inquiries";
import Properties from "@/components/directories/Properties";
import Taxes from "@/components/directories/Taxes";
import Create from "@/components/searches/Create";
import SearchCard from "@/components/searches/Card";
import Edit from "@/components/directories/Edit";

import XMarkIcon from "heroicons/24/outline/x-mark.svg";
import EnvelopeIcon from "heroicons/24/outline/envelope.svg";
import PhoneIcon from "heroicons/24/outline/phone.svg";
import MapPinIcon from "heroicons/24/outline/map-pin.svg";
import PencilIcon from "heroicons/24/outline/pencil-square.svg";
import IdIcon from "heroicons/24/outline/identification.svg";

export default {
  name: "ContactDetailDrawer",

  components: { HeroIcon, Inquiries, Properties, Taxes, Create, SearchCard, Edit },

  data: () => ({
    show: false,
    contactId: null,
    contact: null,
    tab: 0,
    staff: [],
    searches: [],
    success: false,
    XMarkIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    PencilIcon,
    IdIcon
  }),

  computed: {
    roleLabels() {
      const roles = (this.contact && this.contact.roles) || [];
      const seen = new Set();
      const out = [];
      roles.forEach(r => {
        if (r.name && !seen.has(r.name)) {
          seen.add(r.name);
          out.push(this.$te("roles." + r.name) ? this.$t("roles." + r.name) : r.name);
        }
      });
      return out;
    }
  },

  methods: {
    open(contact) {
      this.contactId = contact.id;
      this.contact = null;
      this.tab = 0;
      this.staff = [];
      this.searches = [];
      this.show = true;
      this.fetchContact();
      this.fetchStaff();
      this.fetchSearches();
    },

    refetch() {
      this.fetchContact("no-cache");
      this.fetchSearches();
      this.$emit("reload");
    },

    fetchContact(policy = "cache-first") {
      this.$apollo
        .query({ query: Contact, variables: { contactId: this.contactId }, fetchPolicy: policy })
        .then(res => {
          this.contact = res.data.contact;
        });
    },

    fetchStaff() {
      this.$apollo
        .query({ query: Staff, fetchPolicy: "cache-first" })
        .then(res => {
          this.staff = res.data.staff;
        });
    },

    fetchSearches() {
      this.$apollo
        .query({ query: Searches, variables: { userId: this.contactId }, fetchPolicy: "no-cache" })
        .then(res => {
          this.searches = res.data.searches;
        });
    },

    updateStaff() {
      if (this.contact && this.contact.advisorId) {
        this.$apollo
          .mutate({
            mutation: AttachStaff,
            variables: { input: { id: this.contact.advisorId, userId: this.contact.id } }
          })
          .then(() => this.fetchContact("no-cache"));
      }
    },

    editUser() {
      this.$apollo
        .mutate({
          mutation: EditUser,
          variables: {
            input: {
              id: this.contact.id,
              attributes: { searchObservations: this.contact.searchObservations }
            }
          }
        })
        .then(() => {
          this.success = true;
          setTimeout(() => {
            this.success = false;
          }, 4000);
        });
    },

    openEdit() {
      if (this.$refs.edit) this.$refs.edit.dialog = true;
    }
  }
};
</script>

<style scoped>
.cdrawer >>> .v-navigation-drawer__content { background: #f7f5f1; overflow: hidden; }
.cdrawer__wrap { display: flex; flex-direction: column; height: 100%; }

/* appbar */
.cdrawer__bar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #ece7de;
  background: #fbfaf7;
}
.cdrawer__close {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: #1a1814;
  transition: background 0.15s ease;
}
.cdrawer__close:hover { background: #f1ece3; }
.cdrawer__barname {
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 700;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cdrawer__editbtn {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 8px;
  cursor: pointer;
  color: #1a1814;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 12px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.cdrawer__editbtn:hover { border-color: #dcd4c6; background: #faf8f4; }
.cdrawer__editbtn .hero-icon { color: #6a645a; }

.cdrawer__scroll { flex: 1; min-height: 0; overflow-y: auto; }

/* profile */
.cdrawer__head { padding: 18px 22px 0; }
.cdrawer__roles { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.cdrawer__role {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6a645a;
  background: #ece7de;
  border-radius: 999px;
  padding: 3px 10px;
}
.cdrawer__rows { display: flex; flex-direction: column; gap: 9px; }
.cdrawer__row {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  width: fit-content;
  max-width: 100%;
  font-size: 13.5px;
  color: #56504a;
  text-decoration: none;
}
.cdrawer__row span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cdrawer__row .hero-icon { color: #a39a8c; flex: none; }
.cdrawer__row--link { transition: color 0.15s ease; }
.cdrawer__row--link:hover { color: #b52118; }
.cdrawer__row--link:hover .hero-icon { color: #b52118; }

.cdrawer__section { padding: 0 22px; margin-top: 20px; padding-top: 18px; border-top: 1px solid #ece7de; }
.cdrawer__label {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin-bottom: 10px;
}
.cdrawer__advisor { max-width: 300px; }
.cdrawer__advisor >>> fieldset { border-radius: 10px; }
.cdrawer__advisor-info {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  font-size: 12.5px;
  color: #756f64;
}
.cdrawer__advisor-info .hero-icon { color: #a39a8c; }
.cdrawer__dot { color: #c4bcae; }

/* tabs */
.cdrawer__tabs {
  position: sticky;
  top: 0;
  z-index: 2;
  margin-top: 18px;
  padding: 0 22px;
  background: #f7f5f1;
  border-bottom: 1px solid #ece7de;
}
.cdrawer__tabs >>> .v-tabs-bar { background: transparent; }
.cdrawer__tabs >>> .v-tab {
  text-transform: none;
  letter-spacing: normal;
  font-size: 14px;
  font-weight: 500;
  min-width: 0;
}
/* drop the reserved arrow gutters + the auto-center transform; scroll natively */
.cdrawer__tabs >>> .v-slide-group__prev,
.cdrawer__tabs >>> .v-slide-group__next { display: none !important; }
.cdrawer__tabs >>> .v-slide-group__wrapper { overflow-x: auto; }
.cdrawer__tabs >>> .v-slide-group__content { transform: none !important; }
.cdrawer__tabs >>> .v-slide-group__wrapper::-webkit-scrollbar { display: none; }

.cdrawer__body { padding: 16px 22px 28px; }
.cdrawer__reuse { margin: 0; }

/* búsquedas */
.cdrawer__obs >>> fieldset { border-radius: 10px; }
.cdrawer__obs-actions { display: flex; justify-content: flex-end; margin: 8px 0 14px; }
.cdrawer__searchhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #56504a;
  padding-top: 14px;
  border-top: 1px solid #ece7de;
}
.cdrawer__searchlist { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
</style>
